import type { ReactNode } from 'react'
import './ui.css'

/**
 * Peças de interface reutilizáveis.
 *
 * ─── AS TRÊS REGRAS QUE FAZEM ISTO SER SEGURO ───────────────────────
 *
 * A fronteira de segurança do Clinuma é o banco (RLS) e o escopo na
 * API. A tela é conveniência — ela decide o que é CÔMODO mostrar, nunca
 * o que é PERMITIDO. Dito isso, componente compartilhado tem três
 * armadilhas conhecidas, e cada uma vira uma regra aqui:
 *
 *  1. **Nenhum componente busca dado.** Não há `fetch`, não há `api()`,
 *     não há `useEffect` de carregamento neste arquivo. Um componente
 *     que busca sozinho pode ser montado num contexto onde não deveria,
 *     e ninguém percebe. Aqui todos recebem por prop.
 *
 *  2. **Nenhum componente decide permissão.** Não há `useSessao()` nem
 *     `pode()` aqui. Quem decide é a tela, que conhece o contexto; o
 *     componente recebe `podeEditar` já resolvido. Espalhar a decisão
 *     por dezenas de componentes é como um deles acaba esquecido.
 *
 *  3. **Genérico na forma, específico no conteúdo.** `Indicador` sabe
 *     desenhar um número grande com rótulo — não sabe o que é comissão.
 *     Uma `<Tabela dados={qualquerCoisa}>` que aceita tudo um dia
 *     recebe o que não devia estar ali.
 *
 * O pior que um erro aqui causa é um botão aparecer para quem não pode
 * clicar — e aí a API devolve 403. Feio, não inseguro.
 */

/* ── cabeçalho de tela ──────────────────────────────────────────── */

export function Topo({ titulo, subtitulo, children }: {
  titulo: string
  subtitulo?: string
  /** Controles à direita: período, ações, filtros. */
  children?: ReactNode
}) {
  return (
    <header className="ui-topo">
      <div>
        <h1>{titulo}</h1>
        {subtitulo && <p className="ajuda">{subtitulo}</p>}
      </div>
      {children && <div className="ui-topo__acoes">{children}</div>}
    </header>
  )
}

/* ── navegação de período ───────────────────────────────────────── */

/**
 * O mesmo controle serve para dia e para mês — muda o rótulo do meio e
 * o passo. Duplicar isso em cada tela é como as telas acabam com setas
 * de tamanhos diferentes e comportamentos divergentes na borda do mês.
 */
export function Periodo({
  rotuloAtual, aoAnterior, aoProximo, aoAgora,
  estaNoAgora, futuroBloqueado = false, children,
}: {
  rotuloAtual: string
  aoAnterior(): void
  aoProximo(): void
  aoAgora(): void
  estaNoAgora: boolean
  /** Impede navegar para o futuro — competência fechada, por exemplo. */
  futuroBloqueado?: boolean
  children?: ReactNode
}) {
  return (
    <div className="ui-periodo">
      <button className="botao botao--fantasma botao--pequeno ui-periodo__seta"
              onClick={aoAnterior} aria-label="Período anterior">‹</button>
      <button className="botao botao--fantasma botao--pequeno"
              onClick={aoAgora} disabled={estaNoAgora}>{rotuloAtual}</button>
      <button className="botao botao--fantasma botao--pequeno ui-periodo__seta"
              onClick={aoProximo} disabled={futuroBloqueado}
              aria-label="Próximo período">›</button>
      {children}
    </div>
  )
}

/* ── cartão de número ───────────────────────────────────────────── */

export function Indicador({ rotulo, valor, ajuda, tom = 'neutro', destaque = false }: {
  rotulo: string
  valor: string
  ajuda?: ReactNode
  tom?: 'neutro' | 'ok' | 'erro' | 'atencao'
  /** Marca o cartão como o mais importante do grupo. */
  destaque?: boolean
}) {
  return (
    <div className={`ui-ind ${destaque ? 'is-destaque' : ''}`}>
      <span className="ui-ind__rotulo">{rotulo}</span>
      <strong className={`ui-ind__valor ui-ind__valor--${tom}`}>{valor}</strong>
      {ajuda && <span className="ajuda">{ajuda}</span>}
    </div>
  )
}

export function Indicadores({ children }: { children: ReactNode }) {
  return <div className="ui-inds">{children}</div>
}

/* ── seção com cabeçalho ────────────────────────────────────────── */

export function Secao({ titulo, descricao, acao, resumo, veredito, children }: {
  titulo: string
  descricao?: string
  /** Botão de ação principal da seção. */
  acao?: ReactNode
  /** Número-resumo à direita, quando a seção tem um total que importa. */
  resumo?: { valor: string; ajuda: string }
  /**
   * Selo de veredito, ao lado do TÍTULO.
   *
   * `docs/design-composicao.md` §8: na referência fintech o título diz o
   * assunto e o selo diz se está bem ("Balance ✓ On track"). Tínhamos
   * título e descrição; o veredito responde metade das perguntas antes
   * de a pessoa ler o conteúdo — "✓ completo" numa seção de cadastro
   * poupa conferir campo por campo.
   *
   * É ESTADO, então é pílula tingida — ao contrário do chip com borda,
   * que é atributo.
   */
  veredito?: { texto: string; tom: 'ok' | 'atencao' | 'erro' | 'neutro' }
  children: ReactNode
}) {
  return (
    <section className="cartao ui-secao">
      <header className="ui-secao__cab">
        <div>
          <div className="ui-secao__linha-titulo">
            <h2>{titulo}</h2>
            {veredito && (
              <span className={`selo selo--${veredito.tom} ui-secao__veredito`}>
                {veredito.texto}
              </span>
            )}
          </div>
          {descricao && <p className="ajuda">{descricao}</p>}
        </div>
        {resumo && (
          <div className="ui-secao__resumo">
            <strong className="mono">{resumo.valor}</strong>
            <span className="ajuda">{resumo.ajuda}</span>
          </div>
        )}
        {acao}
      </header>
      {children}
    </section>
  )
}

/* ── estado vazio ───────────────────────────────────────────────── */

/**
 * Um estado vazio diz o que fazer a seguir. O concorrente desenha dez
 * linhas fantasma numa grade zebrada quando não há dado — o usuário fica
 * sem saber se a lista está vazia, se o filtro não pegou nada ou se a
 * tela quebrou.
 */
export function Vazio({ mensagem, sugestao, acao }: {
  mensagem: string
  sugestao?: string
  acao?: ReactNode
}) {
  return (
    <div className="ui-vazio">
      <p className="ui-vazio__msg">{mensagem}</p>
      {sugestao && <p className="ajuda">{sugestao}</p>}
      {acao && <div className="ui-vazio__acao">{acao}</div>}
    </div>
  )
}

/* ── estado do agendamento ──────────────────────────────────────── */

export type Estado =
  | 'AGENDADO' | 'CONFIRMADO' | 'CHEGOU' | 'ATENDENDO' | 'ATENDIDO'
  | 'FALTOU' | 'CANCELADO_PACIENTE' | 'CANCELADO_CLINICA' | 'REMARCADO'

/**
 * Rótulo e cor de cada estado, num lugar só.
 *
 * `CANCELADO_PACIENTE` vira "Desmarcou" e `FALTOU` vira "Faltou", com
 * cores distintas — âmbar para quem avisou, vermelho para quem não
 * apareceu. Não é preciosismo: os usuários do concorrente pedem essa
 * distinção em público na loja de aplicativos, porque uma é aviso e a
 * outra é abandono, e a cobrança da recepção muda conforme.
 */
const ESTADOS: Record<Estado, { rotulo: string; classe: string }> = {
  AGENDADO:           { rotulo: 'Agendado',       classe: 'ag' },
  CONFIRMADO:         { rotulo: 'Confirmado',     classe: 'ok' },
  CHEGOU:             { rotulo: 'Chegou',         classe: 'rc' },
  ATENDENDO:          { rotulo: 'Em atendimento', classe: 'at' },
  ATENDIDO:           { rotulo: 'Atendido',       classe: 'fi' },
  FALTOU:             { rotulo: 'Faltou',         classe: 'er' },
  CANCELADO_PACIENTE: { rotulo: 'Desmarcou',      classe: 'dm' },
  CANCELADO_CLINICA:  { rotulo: 'Cancelado',      classe: 'dm' },
  REMARCADO:          { rotulo: 'Remarcado',      classe: 'rm' },
}

/** Estados que não mudam mais — usados para esmaecer sem esconder. */
export const ENCERRADO: ReadonlySet<string> = new Set([
  'ATENDIDO', 'FALTOU', 'CANCELADO_PACIENTE', 'CANCELADO_CLINICA', 'REMARCADO',
])

export function SeloEstado({ estado }: { estado: string }) {
  const e = ESTADOS[estado as Estado] ?? { rotulo: estado, classe: 'ag' }
  return <span className={`selo ui-st ui-st--${e.classe}`}>{e.rotulo}</span>
}

/* ── progresso de meta ──────────────────────────────────────────── */

/**
 * Passar de 100% é notícia boa: a barra para de crescer, mas o número
 * continua contando a verdade.
 */
export function Progresso({ pct, rotulo }: { pct: number; rotulo?: string }) {
  const largura = Math.max(0, Math.min(100, pct))
  const classe = pct >= 100 ? 'is-batida' : pct >= 70 ? 'is-perto' : 'is-longe'
  return (
    <div className={`ui-barra ${classe}`}
         role="img" aria-label={rotulo ?? `${pct.toFixed(0)}% da meta`}>
      <i style={{ width: `${largura}%` }} />
    </div>
  )
}

/* ── folha / modal ──────────────────────────────────────────────── */

/**
 * No celular sobe do pé da tela, onde o polegar alcança; no desktop
 * vira caixa centrada. É o mesmo componente porque é o mesmo conceito —
 * mudar de comportamento por largura é CSS, não outro componente.
 */
export function Folha({ titulo, descricao, aoFechar, children, acoes }: {
  titulo: string
  descricao?: string
  aoFechar(): void
  children: ReactNode
  acoes: ReactNode
}) {
  const id = `folha-${titulo.replace(/\W+/g, '-').toLowerCase()}`
  return (
    <div className="ui-folha" role="dialog" aria-modal="true" aria-labelledby={id}
         onClick={(e) => { if (e.target === e.currentTarget) aoFechar() }}>
      <div className="ui-folha__caixa cartao">
        <h2 id={id}>{titulo}</h2>
        {descricao && <p className="ajuda">{descricao}</p>}
        {children}
        <div className="ui-folha__acoes">{acoes}</div>
      </div>
    </div>
  )
}

/* ── esqueleto de carregamento ──────────────────────────────────── */

export function Carregando({ altura = 120 }: { altura?: number }) {
  return <div className="esqueleto" style={{ height: altura }} aria-hidden="true" />
}

/* ── aviso de erro ──────────────────────────────────────────────── */

export function Erro({ mensagem }: { mensagem: string | null }) {
  if (!mensagem) return null
  return <div className="aviso aviso--erro" role="alert">{mensagem}</div>
}
